import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import host from "../../global";
import HostCodingProblem from "../HostCodingProblem";
import HostApexProblem from "../HostApexProblem";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../hooks/useTheme";
import {
  ArrowLeft, Layers, Sparkles, ChevronDown, Save, Loader2,
  Trash2, AlertTriangle, Plus, X, Shuffle,
} from "lucide-react";
import StepIndicator from "./mcq/StepIndicator";
import ProblemCard from "./coding/ProblemCard";
import ProblemDetailModal from "./coding/ProblemDetailModal";
import { DSA_TYPES, APEX_TOPICS, APEX_TRACK, APEX_LANGUAGE } from "./coding/codingConstants";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import DropDownWithSearch from "../shared/DropDownWIthSearch";

const STEPS = ["Details and Questions", "Configuration", "Review"];

const DIFF_FILTER_OPTIONS = [
  { label: "All", value: "All" },
  { label: "Easy", value: "Easy" },
  { label: "Medium", value: "Medium" },
  { label: "Hard", value: "Hard" },
  { label: "Custom", value: "Custom" },
];

// Problems must carry one of these tags to be shown; self-hosted problems are
// always surfaced under "Custom" regardless of their raw difficulty field.
const VALID_DIFFICULTIES = ["Easy", "Medium", "Hard"];

const TIME_LIMIT_OPTIONS = [15, 30, 45, 60, 75, 90, 120].map((m) => ({ label: `${m} min`, value: m }));
const DEFAULT_TIME_LIMIT = 30;
const CODING_DIFF_TIME = { Easy: 10, Medium: 20, Hard: 30 };

const SUPPORTED_LANGUAGES = ["Python", "Java", "C++", "C#", "JavaScript", "Go", "Rust"];
// A new set may pick Apex, but Apex is exclusive: it can never be combined with another language.
const NEW_SET_LANGUAGES = [...SUPPORTED_LANGUAGES, APEX_LANGUAGE];
const DEFAULT_LANGUAGES = ["Python", "Java", "C++"];

const DIFF_COLORS = {
  Easy:   { bg: "bg-emerald-50 dark:bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-200 dark:border-emerald-500/20" },
  Medium: { bg: "bg-amber-50 dark:bg-amber-500/10",     text: "text-amber-600 dark:text-amber-400",   border: "border-amber-200 dark:border-amber-500/20" },
  Hard:   { bg: "bg-rose-50 dark:bg-rose-500/10",       text: "text-rose-600 dark:text-rose-400",     border: "border-rose-200 dark:border-rose-500/20" },
  Custom: { bg: "bg-violet-50 dark:bg-violet-500/10",   text: "text-violet-600 dark:text-violet-400", border: "border-violet-200 dark:border-violet-500/20" },
};

const DiffBadge = ({ difficulty }) => {
  const c = DIFF_COLORS[difficulty] || DIFF_COLORS.Custom;
  return (
    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${c.bg} ${c.text} ${c.border}`}>
      {difficulty || "—"}
    </span>
  );
};

const formatUpdatedAt = (meta) => {
  const ts = meta?.updated_at || meta?.created_at;
  if (!ts) return null;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const initialFormData = {
  title: "",
  description: "",
  input_format: "",
  output_format: "",
  constraints: "",
  examples: [{ input: "", output: "", explanation: "" }],
  test_cases: [{ input: "", output: "" }],
  final_test_cases: [{ input: "", output: "" }],
  boilerplate_code: {},
  dsa_types: [],
};

const CreateProblem = () => {
  const accessToken = sessionStorage.getItem("accessToken");
  const navigate = useNavigate();
  const { theme } = useTheme();

  // ── Page-level view: "list" (existing sets grid) or "wizard" (create/edit) ───
  const [view, setView] = useState("list");

  // ── Core set state ───────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [newSetInput, setNewSetInput] = useState("");
  const [sets, setSets] = useState([]);
  const [setsData, setSetsData] = useState({});
  const [selectedSet, setSelectedSet] = useState("");

  // ── Step 1 config ────────────────────────────────────────────────────────────
  const [allowedLanguages, setAllowedLanguages] = useState(DEFAULT_LANGUAGES);
  const [step1SubmitAttempted, setStep1SubmitAttempted] = useState(false);

  // ── Step 2 config ────────────────────────────────────────────────────────────
  const [codingTimeLimit, setCodingTimeLimit] = useState(DEFAULT_TIME_LIMIT);

  // ── Questions (slot-based; each slot = one "Question N" that can hold 1+ problems) ──
  const [questionSlots, setQuestionSlots] = useState([]); // [{ id, problemIds: [] }]
  const [expandedSlotIdx, setExpandedSlotIdx] = useState(null);
  const [dragOverSlotIdx, setDragOverSlotIdx] = useState(null);
  const [slotDiffFilter, setSlotDiffFilter] = useState("All");
  const [slotTypeFilters, setSlotTypeFilters] = useState([]); // [] = no filter (all types)

  // ── Problem pools (shared across all question slots) ─────────────────────────
  const [selectedProblems, setSelectedProblems] = useState([]);
  const [availableProblems, setAvailableProblems] = useState([]);
  const [selfHostedProblems, setSelfHostedProblems] = useState([]);
  const [codingProblemOrigins, setCodingProblemOrigins] = useState({});

  // ── Custom problem form ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState(initialFormData);
  const [formVisible, setFormVisible] = useState(false);
  const [apexFormVisible, setApexFormVisible] = useState(false);

  // ── Detail modal ─────────────────────────────────────────────────────────────
  const [detailProblem, setDetailProblem] = useState(null);
  const [detailAction, setDetailAction] = useState("add");
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRequestRef = useRef(0); // bumped on close/re-open so a stale fetch can't reopen the modal
  const libraryRequestRef = useRef(0); // bumped per library load so a slow earlier response can't win

  // ── Loading / delete ─────────────────────────────────────────────────────────
  const [isFetchingSets, setIsFetchingSets] = useState(true);
  const [isLoadingProblems, setIsLoadingProblems] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // ── Derived ──────────────────────────────────────────────────────────────────
  // Apex is exclusive, so "the set contains Apex" is the same thing as "this is an Apex set".
  const isApex = allowedLanguages.includes(APEX_LANGUAGE);
  const track = isApex ? APEX_TRACK : "dsa";
  const topicOptions = isApex ? APEX_TOPICS : DSA_TYPES;
  // An existing set can't change type (the server rejects it), so lock the picker.
  const languageOptions = selectedSet ? SUPPORTED_LANGUAGES : NEW_SET_LANGUAGES;
  const assignedIds = new Set(selectedProblems.map((p) => p.problem_id));
  const libraryAvailable = availableProblems.filter(
    (p) => !assignedIds.has(p.problem_id) && VALID_DIFFICULTIES.includes(p.difficulty)
  );
  // Self-hosted problems are always tagged "Custom" in the UI, so they're never untagged.
  const librarySelfHosted = selfHostedProblems.filter((p) => !assignedIds.has(p.problem_id));
  const activeSetName = selectedSet || newSetInput.trim();

  // ── Step 1 validation — derived live so errors clear themselves once fixed ───
  const emptySlotIndexes = questionSlots.reduce((acc, slot, idx) => {
    if (slot.problemIds.length === 0) acc.push(idx);
    return acc;
  }, []);
  const setNameInvalid = step1SubmitAttempted && !activeSetName;
  const languageInvalid = step1SubmitAttempted && allowedLanguages.length === 0;
  const questionsInvalid = step1SubmitAttempted && emptySlotIndexes.length > 0;

  const slotFiltersApplied = slotDiffFilter !== "All" || slotTypeFilters.length > 0;

  const slotLibrary = useMemo(() => {
    const matches = (p, isSelf) => {
      if (slotDiffFilter !== "All") {
        if (slotDiffFilter === "Custom") { if (!isSelf) return false; }
        else if (isSelf || p.difficulty !== slotDiffFilter) return false;
      }
      if (slotTypeFilters.length > 0) {
        // Apex problems are tagged with Apex topics; DSA problems with DSA types.
        const tags = isApex ? p.topics : p.dsa_types;
        const types = tags?.length ? tags : ["Uncategorized"];
        if (!types.some((t) => slotTypeFilters.includes(t))) return false;
      }
      return true;
    };
    return {
      available: libraryAvailable.filter((p) => matches(p, false)),
      selfHosted: librarySelfHosted.filter((p) => matches(p, true)),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [libraryAvailable, librarySelfHosted, slotDiffFilter, slotTypeFilters, isApex]);

  // Worst-case time per question (a candidate gets one random problem per slot),
  // summed across all questions — a rough, informational suggestion only.
  const suggestedTimeLimit = useMemo(() => {
    const total = questionSlots.reduce((sum, slot) => {
      if (slot.problemIds.length === 0) return sum;
      const times = slot.problemIds.map((pid) => {
        const p = selectedProblems.find((sp) => sp.problem_id === pid);
        if (!p) return 20;
        return p.estimated_time ?? (codingProblemOrigins[pid] === "selfHosted" ? 20 : (CODING_DIFF_TIME[p.difficulty] || 20));
      });
      return sum + Math.max(...times);
    }, 0);
    return total > 0 ? Math.ceil(total / 5) * 5 : 0;
  }, [questionSlots, selectedProblems, codingProblemOrigins]);

  // ── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchSets(); // eslint-disable-next-line
  }, []);

  const authHeader = { Authorization: `Bearer ${accessToken}` };

  const fetchSets = async () => {
    setIsFetchingSets(true);
    try {
      const res = await axios.get(`${host}/api/codingsets/`, { headers: authHeader });
      const arr = Array.isArray(res.data) ? res.data : [];
      setSets(arr.map((s) => s.set_name));
      const byName = {};
      arr.forEach((s) => { byName[s.set_name] = s; });
      setSetsData(byName);
    } catch {
      toast.error("Error fetching sets");
    } finally {
      setIsFetchingSets(false);
    }
  };

  // ── Select existing set → pre-fill ──────────────────────────────────────────
  const handleSelectExistingSet = (s) => {
    setSelectedSet(s);
    setNewSetInput("");
    const raw = setsData[s];
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      setAllowedLanguages(
        raw.track === APEX_TRACK
          ? [APEX_LANGUAGE]
          : (raw.allowed_languages?.length ? raw.allowed_languages : DEFAULT_LANGUAGES)
      );
      setCodingTimeLimit(raw.time_limit_minutes || DEFAULT_TIME_LIMIT);
    } else {
      setAllowedLanguages(DEFAULT_LANGUAGES);
      setCodingTimeLimit(DEFAULT_TIME_LIMIT);
    }
  };

  // ── Language picker (Apex is exclusive and switches the whole library) ───────
  const resetToEmptyQuestions = () => {
    setSelectedProblems([]);
    setCodingProblemOrigins({});
    setSlotDiffFilter("All");
    setSlotTypeFilters([]);
    setExpandedSlotIdx(0);
    setStep1SubmitAttempted(false);
    setQuestionSlots([{ id: `${Date.now()}`, problemIds: [] }]);
  };

  const handleLanguageChange = (val) => {
    const next = (val ? val.split(",").map((v) => v.trim()) : []).filter((v) => v && v !== "All");
    const hadApex = allowedLanguages.includes(APEX_LANGUAGE);

    let resolved = next;
    if (!hadApex && next.includes(APEX_LANGUAGE)) resolved = [APEX_LANGUAGE];           // picking Apex drops the rest
    else if (hadApex && next.length > 1) resolved = next.filter((l) => l !== APEX_LANGUAGE); // picking another drops Apex

    const willBeApex = resolved.includes(APEX_LANGUAGE);
    if (willBeApex === hadApex) { setAllowedLanguages(resolved); return; }

    // The question library differs per track, so the questions picked so far no longer apply.
    if (selectedProblems.length > 0 && !window.confirm(
      `Switching to ${willBeApex ? "Apex" : "a standard language"} uses a different question library. ` +
      "Your selected questions will be cleared. Continue?"
    )) return;

    setAllowedLanguages(resolved);
    resetToEmptyQuestions();
    loadProblemLibrary(undefined, willBeApex ? APEX_TRACK : "dsa");
  };

  // ── Shared: fetch library + (optionally) an existing set's problems/slots ───
  const loadProblemLibrary = async (existingSetName, libraryTrack = "dsa") => {
    // Only the latest call may write state: switching language quickly must not let a
    // slow response for the previous track overwrite the current library.
    const requestId = ++libraryRequestRef.current;
    const isStale = () => requestId !== libraryRequestRef.current;
    setIsLoadingProblems(true);
    try {
      const res = await axios.get(`${host}/api/available_problems`, {
        headers: authHeader,
        params: { track: libraryTrack },
      });
      if (isStale()) return;
      let allAvailable = res.data.available_problems;
      let allSelfHosted = res.data.self_hosted;

      if (existingSetName) {
        const codingRes = await axios.post(
          `${host}/api/coding-problems`,
          { set_name: existingSetName },
          { headers: authHeader }
        );
        if (isStale()) return;
        const setProblems = codingRes.data;
        setSelectedProblems(setProblems);

        const origins = {};
        setProblems.forEach((p) => {
          origins[p.problem_id] = allSelfHosted.some((s) => s.problem_id === p.problem_id)
            ? "selfHosted"
            : "available";
        });
        setCodingProblemOrigins(origins);

        const setIds = new Set(setProblems.map((p) => p.problem_id));
        allAvailable = allAvailable.filter((p) => !setIds.has(p.problem_id));
        allSelfHosted = allSelfHosted.filter((p) => !setIds.has(p.problem_id));

        // Sets saved with question_slots already had their accordion structure
        // set synchronously by the caller (handleOpenExistingSet) — this is only
        // needed as a fallback for legacy sets that predate question_slots.
        const savedSlots = setsData[existingSetName]?.question_slots;
        if (!(Array.isArray(savedSlots) && savedSlots.length > 0)) {
          setQuestionSlots(
            setProblems.length > 0
              ? setProblems.map((p) => ({ id: p.problem_id, problemIds: [p.problem_id] }))
              : [{ id: `${Date.now()}`, problemIds: [] }]
          );
        }
      } else {
        setSelectedProblems([]);
        setCodingProblemOrigins({});
      }

      setAvailableProblems(allAvailable);
      setSelfHostedProblems(allSelfHosted);
    } catch {
      if (!isStale()) toast.error("Error loading problems");
    } finally {
      if (!isStale()) setIsLoadingProblems(false);
    }
  };

  // ── Step 1 → Step 2 ──────────────────────────────────────────────────────────
  const handleStep1Next = () => {
    setStep1SubmitAttempted(true);
    let hasError = false;

    if (!selectedSet && !newSetInput.trim()) {
      hasError = true;
      toast.warn("Please enter a set name.");
    }
    if (allowedLanguages.length === 0) {
      hasError = true;
      toast.warn("Please select at least one coding language.");
    }
    if (emptySlotIndexes.length > 0) {
      hasError = true;
      const names = emptySlotIndexes.map((i) => `Question ${i + 1}`).join(", ");
      toast.warn(`Add at least 1 problem to ${names}.`);
    }

    if (hasError) return;
    setStep(2);
  };

  // ── Entry points from the list view ─────────────────────────────────────────
  const handleOpenCreateSet = () => {
    setSelectedSet("");
    setNewSetInput("");
    setAllowedLanguages(DEFAULT_LANGUAGES);
    setCodingTimeLimit(DEFAULT_TIME_LIMIT);
    setSelectedProblems([]);
    setCodingProblemOrigins({});
    setSlotDiffFilter("All");
    setSlotTypeFilters([]);
    setExpandedSlotIdx(0); // Question 1 starts open
    setStep1SubmitAttempted(false);
    // Known up-front — render the accordion immediately instead of waiting on the fetch.
    setQuestionSlots([{ id: `${Date.now()}`, problemIds: [] }]);
    setStep(1);
    setView("wizard");
    loadProblemLibrary();
  };

  const handleOpenExistingSet = (setName) => {
    handleSelectExistingSet(setName);
    setExpandedSlotIdx(0); // Question 1 starts open
    setStep1SubmitAttempted(false);
    // Sets saved with question_slots already know their accordion shape — render
    // it immediately. Legacy sets without slots fall back to [] (skeleton) until
    // loadProblemLibrary resolves and can synthesize slots from the fetched problems.
    const savedSlots = setsData[setName]?.question_slots;
    setQuestionSlots(
      Array.isArray(savedSlots) && savedSlots.length > 0
        ? savedSlots.map((s, i) => ({ id: s.id || `${Date.now()}-${i}`, problemIds: s.problem_ids || [] }))
        : []
    );
    setSlotDiffFilter("All");
    setSlotTypeFilters([]);
    setStep(1);
    setView("wizard");
    loadProblemLibrary(setName, setsData[setName]?.track === APEX_TRACK ? APEX_TRACK : "dsa");
  };

  // ── Add / remove ─────────────────────────────────────────────────────────────
  const handleAdd = (problem, slotIdx) => {
    if (slotIdx == null) return;
    setSelectedProblems((prev) => [...prev, problem]);
    const isSelfHosted = selfHostedProblems.some((p) => p.problem_id === problem.problem_id);
    if (isSelfHosted) {
      setSelfHostedProblems((prev) => prev.filter((p) => p.problem_id !== problem.problem_id));
    } else {
      setAvailableProblems((prev) => prev.filter((p) => p.problem_id !== problem.problem_id));
    }
    setCodingProblemOrigins((prev) => ({
      ...prev,
      [problem.problem_id]: isSelfHosted ? "selfHosted" : "available",
    }));
    setQuestionSlots((prev) => prev.map((s, i) => (i === slotIdx ? { ...s, problemIds: [...s.problemIds, problem.problem_id] } : s)));
  };

  // Bookkeeping shared by handleRemove / handleDeleteProblem / removeQuestionSlot —
  // restores a problem to its library pool without touching question slots.
  const detachProblem = (problem) => {
    setSelectedProblems((prev) => prev.filter((p) => p.problem_id !== problem.problem_id));
    const origin = codingProblemOrigins[problem.problem_id];
    
    if (origin === 'selfHosted') {
      setSelfHostedProblems([...selfHostedProblems, problem]);
    } else {
      setAvailableProblems([...availableProblems, problem]);
    }
    setCodingProblemOrigins((prev) => {
      const next = { ...prev };
      delete next[problem.problem_id];
      return next;
    });
  };

  const handleRemove = (problem) => {
    detachProblem(problem);
    setQuestionSlots((prev) => prev.map((s) => ({ ...s, problemIds: s.problemIds.filter((id) => id !== problem.problem_id) })));
  };

  const handleDeleteProblem = async (problem) => {
    try {
      await axios.delete(`${host}/api/delete-coding-problem`, {
        params: { problem_id: problem.problem_id },
        headers: authHeader,
      });
      setSelfHostedProblems((prev) => prev.filter((p) => p.problem_id !== problem.problem_id));
      setSelectedProblems((prev) => prev.filter((p) => p.problem_id !== problem.problem_id));
      setCodingProblemOrigins((prev) => {
        const next = { ...prev };
        delete next[problem.problem_id];
        return next;
      });
      setQuestionSlots((prev) => prev.map((s) => ({ ...s, problemIds: s.problemIds.filter((id) => id !== problem.problem_id) })));
      toast.success(`"${problem.title}" deleted`);
    } catch {
      toast.error("Failed to delete problem");
    }
  };

  // ── Question slots ───────────────────────────────────────────────────────────
  const toggleSlot = (idx) => {
    if (expandedSlotIdx === idx) {
      setExpandedSlotIdx(null);
    } else {
      setExpandedSlotIdx(idx);
      setSlotDiffFilter("All");
      setSlotTypeFilters([]);
    }
  };

  const addQuestionSlot = () => {
    setExpandedSlotIdx(questionSlots.length);
    setSlotDiffFilter("All");
    setSlotTypeFilters([]);
    setQuestionSlots((prev) => [...prev, { id: `${Date.now()}-${prev.length}`, problemIds: [] }]);
  };

  const removeQuestionSlot = (idx) => {
    const slot = questionSlots[idx];
    if (slot) {
      slot.problemIds.forEach((pid) => {
        const prob = selectedProblems.find((p) => p.problem_id === pid);
        if (prob) detachProblem(prob);
      });
    }
    setQuestionSlots((prev) => prev.filter((_, i) => i !== idx));
    setExpandedSlotIdx((cur) => {
      if (cur === idx) return null;
      if (cur !== null && cur > idx) return cur - 1;
      return cur;
    });
  };

  // A slot has no single "difficulty" of its own (its filters are transient) — pick a
  // representative label from its assigned problems for the saved doc: "Custom" if any
  // problem is self-hosted, else the hardest difficulty present.
  const DIFF_RANK = { Easy: 1, Medium: 2, Hard: 3 };
  const computeSlotDifficulty = (slot) => {
    const problems = slot.problemIds
      .map((pid) => selectedProblems.find((p) => p.problem_id === pid))
      .filter(Boolean);
    if (problems.some((p) => codingProblemOrigins[p.problem_id] === "selfHosted")) return "Custom";
    const hardest = problems.reduce((acc, p) => {
      const rank = DIFF_RANK[p.difficulty] || 0;
      return rank > acc.rank ? { rank, difficulty: p.difficulty } : acc;
    }, { rank: 0, difficulty: null });
    return hardest.difficulty || "Medium";
  };

  // ── Save ─────────────────────────────────────────────────────────────────────
  const handleSaveSet = async () => {
    const name = selectedSet || newSetInput.trim();
    if (!name) return;
    setIsSaving(true);
    try {
      await axios.post(
        `${host}/api/create-set`,
        {
          name,
          problem_ids: selectedProblems.map((p) => p.problem_id),
          allowed_languages: allowedLanguages,
          question_slots: questionSlots
            .filter((s) => s.problemIds.length > 0)
            .map((s) => ({ id: s.id, difficulty: computeSlotDifficulty(s), problem_ids: s.problemIds })),
          time_limit_minutes: codingTimeLimit,
          track,
        },
        { headers: authHeader }
      );
      toast.success(selectedSet ? "Set saved successfully!" : "Set created successfully!");
      setSelectedSet("");
      setNewSetInput("");
      fetchSets();
      setView("list");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail ? detail : "Error saving set");
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────────
  const handleDeleteSet = (setName) => setDeleteTarget(setName);
  const confirmDelete = async () => {
    const setName = deleteTarget;
    setDeleteTarget(null);
    try {
      await axios.delete(`${host}/api/delete-coding-set`, {
        params: { set_name: setName },
        headers: authHeader,
      });
      toast.success(`"${setName}" deleted`);
      if (selectedSet === setName) setSelectedSet("");
      fetchSets();
    } catch {
      toast.error("Error deleting set");
    }
  };

  // ── Detail modal ─────────────────────────────────────────────────────────────
  const handleViewDetails = async (problem, action) => {
    const requestId = ++detailRequestRef.current;
    setDetailAction(action);
    setDetailLoading(true);
    setDetailProblem({ problem_id: problem.problem_id, title: problem.title });
    try {
      const res = await axios.get(`${host}/api/problem/${problem.problem_id}`, { headers: authHeader });
      if (detailRequestRef.current !== requestId) return; // modal was closed (or another problem opened) meanwhile
      setDetailProblem(res.data);
    } catch { /* keep stub */ } finally {
      if (detailRequestRef.current === requestId) setDetailLoading(false);
    }
  };

  const handleCloseDetailModal = () => {
    detailRequestRef.current++; // invalidate any in-flight fetch so it can't reopen the modal
    setDetailProblem(null);
    setDetailLoading(false);
  };

  // ── Custom problem submit ─────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = selectedSet || newSetInput.trim();
    try {
      await axios.post(
        `${host}/api/createproblems`,
        { ...formData, set_name: name },
        { headers: authHeader }
      );
      toast.success("Problem created successfully!");
      setFormData(initialFormData);
      setFormVisible(false);
      const res = await axios.get(`${host}/api/available_problems`, { headers: authHeader });
      setAvailableProblems(res.data.available_problems.filter((p) => !assignedIds.has(p.problem_id)));
      setSelfHostedProblems(res.data.self_hosted.filter((p) => !assignedIds.has(p.problem_id)));
    } catch {
      toast.error("Error creating problem");
    }
  };

  // ── Custom Apex question submit (used by HostApexProblem) ──────────────────────
  const handleApexSubmit = async (payload) => {
    try {
      await axios.post(`${host}/api/createproblems`, payload, { headers: authHeader });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      return { ok: false, error: typeof detail === "string" && detail ? detail : "Could not save the question." };
    }
    toast.success("Question created successfully!");
    setApexFormVisible(false);
    try {
      const res = await axios.get(`${host}/api/available_problems`, { headers: authHeader, params: { track: APEX_TRACK } });
      setAvailableProblems(res.data.available_problems.filter((p) => !assignedIds.has(p.problem_id)));
      setSelfHostedProblems(res.data.self_hosted.filter((p) => !assignedIds.has(p.problem_id)));
    } catch {
      toast.warn("Question saved, but the list could not be refreshed. Reopen the set to see it.");
    }
    return { ok: true };
  };

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Scoped to this page only — MultiSelectDropdown is shared elsewhere and keeps its default radius there. */}
      <style>{`
        .pill-multiselect > div > div:first-child { border-radius: 30px !important; }
        .coding-lang-select > div > div:first-child { padding-top: 8px !important; padding-bottom: 8px !important; }
        .coding-type-select > div > div:first-child { padding-top: 8px !important; padding-bottom: 8px !important; }
        .coding-lang-error > div > div:first-child { border-color: #f87171 !important; }
      `}</style>
      <div className="min-h-screen">
        <div className="bg-bgPrimary flex flex-col min-h-screen border border-gray-700/50 rounded-2xl pt-4 pb-16">

          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-2 mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  if (view === "wizard") {
                    if (step > 1) { setStep((s) => s - 1); return; }
                    setView("list");
                    return;
                  }
                  navigate(-1);
                }}
                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 dark:text-[#C9A3FF] text-textSecondary" />
              </button>
              <span className="dark:text-gray-400 text-textSecondary">Coding Problem Sets</span>
            </div>
            {view === "list" && (
              <button
                onClick={handleOpenCreateSet}
                className="button flex items-center gap-2 px-6 py-2.5"
              >
                <span><Plus size={16} /></span>
                <span>Create Set</span>
              </button>
            )}
          </div>

          {/* ════════════════════════════════════════════════════════════════
              LIST VIEW — All existing sets
          ════════════════════════════════════════════════════════════════ */}
          {view === "list" && (
            <div className="px-6 flex-1 flex flex-col pb-6">
              <div className="flex-1 rounded-2xl dark:glass-effect bg-bgPrimary border border-gray-700/50 dark:shadow-lg shadow-sm p-6 overflow-y-auto">
                {isFetchingSets ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                      <div key={i} className="h-[92px] rounded-2xl bg-gray-100 dark:bg-white/[0.06] animate-pulse" />
                    ))}
                  </div>
                ) : sets.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full gap-3 text-center py-24">
                    <div className="w-16 h-16 rounded-2xl bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                      <Layers size={28} className="text-blue-300 dark:text-blue-500/60" />
                    </div>
                    <div>
                      <p className="text-base font-medium dark:text-gray-300 text-gray-600">No coding sets yet</p>
                      <p className="text-sm dark:text-gray-500 text-gray-400 mt-1">Click "Create Set" to build your first one</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {sets.map((s) => {
                      const meta = setsData[s];
                      const qCount = meta?.problem_count ?? 0;
                      const updatedLabel = formatUpdatedAt(meta);
                      return (
                        <div
                          key={s}
                          className="group relative rounded-2xl dark:glass-effect bg-bgPrimary border border-gray-700/50 hover:border-gray-400 dark:hover:border-white/30 dark:shadow-lg shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <button
                            onClick={() => handleOpenExistingSet(s)}
                            className="w-full flex items-start gap-2.5 p-4 text-left"
                          >
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100 dark:bg-white/[0.06] group-hover:bg-gradient-to-br group-hover:from-pink-500/20 group-hover:to-purple-500/20 transition-colors">
                              <Layers size={15} className="text-gray-400 dark:text-gray-500 group-hover:text-pink-500 dark:group-hover:text-pink-400" />
                            </div>
                            <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <p className="text-base font-semibold dark:text-white text-gray-800 truncate">{s}</p>
                                {meta?.track === APEX_TRACK && (
                                  <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 border border-sky-200 dark:border-sky-500/20">Apex</span>
                                )}
                              </div>
                              <span className="text-xs text-gray-400 dark:text-gray-500">{qCount} {qCount === 1 ? "problem" : "problems"}</span>
                              {updatedLabel && <span className="text-xs text-gray-400 dark:text-gray-500 truncate">Updated {updatedLabel}</span>}
                            </div>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeleteSet(s); }}
                            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center text-gray-300 dark:text-gray-600 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-all duration-150 z-10"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ════════════════════════════════════════════════════════════════
              WIZARD VIEW — Create / edit a set
          ════════════════════════════════════════════════════════════════ */}
          {view === "wizard" && (
          <>
          <StepIndicator steps={STEPS} currentStep={step} onStepClick={(n) => { if (n < step) setStep(n); }} separator="chevron" />

          <div className="px-16 flex-1">

            {/* ════════════════════════════════════════════════════════════════
                STEP 1 — Set Up
            ════════════════════════════════════════════════════════════════ */}
            {step === 1 && (
              <div className="flex flex-col">
                <div className="w-full py-6 space-y-3">

                    {/* Set Name + Coding language */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
                      <div className="space-y-2">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Set Name <span className="text-red-500">*</span></p>
                        {selectedSet ? (
                          <div className="flex items-center gap-2 px-4 py-3 rounded-[30px] dark:glass-effect bg-bgPrimary border border-gray-500">
                            <Layers size={14} className="dark:text-[#C9A3FF] text-textSecondary flex-shrink-0" />
                            <p className="text-sm font-semibold dark:text-white text-gray-800 truncate">{selectedSet}</p>
                            <span className="text-[11px] text-gray-400 ml-auto">editing</span>
                          </div>
                        ) : (
                          <>
                            <input
                              type="text" value={newSetInput}
                              onChange={(e) => setNewSetInput(e.target.value)}
                              onKeyDown={(e) => e.key === "Enter" && handleStep1Next()}
                              placeholder="e.g. Backend Round 1, DSA Basics…"
                              maxLength={60}
                              className={`w-full dark:glass-effect dark:bg-none bg-bgPrimary px-4 py-3 rounded-[30px] dark:text-white text-textSecondary font-light text-sm focus:outline-none focus:ring-2 focus:ring-[#5A4479] placeholder:text-gray-400 dark:placeholder:text-gray-500 border transition-all duration-200 ${setNameInvalid ? "border-red-400 dark:border-red-500/60 ring-1 ring-red-400/30" : "border-gray-500"}`}
                            />
                            <div className="flex justify-end">
                              <span className={`text-[11px] font-medium tabular-nums ${newSetInput.length > 50 ? "text-rose-400" : "text-gray-400 dark:text-gray-600"}`}>{newSetInput.length}/60</span>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Coding language <span className="text-red-500">*</span></p>
                        {selectedSet && isApex ? (
                          <div className="flex items-center gap-2 px-4 py-3 rounded-[30px] dark:glass-effect bg-bgPrimary border border-gray-500">
                            <p className="text-sm font-semibold dark:text-white text-gray-800">Apex</p>
                            <span className="text-[11px] text-gray-400 ml-auto">fixed for this set</span>
                          </div>
                        ) : (
                          <div className={`pill-multiselect coding-lang-select ${languageInvalid ? "coding-lang-error" : ""}`}>
                            <MultiSelectDropdown
                              options={languageOptions}
                              value={allowedLanguages.join(", ")}
                              onChange={handleLanguageChange}
                              placeholder="Select languages"
                              searchPlaceholder="Search languages…"
                            />
                          </div>
                        )}
                        {languageInvalid && (
                          <p className="text-[11px] text-rose-400">At least one language is required.</p>
                        )}
                        {isApex && !selectedSet && (
                          <p className="text-[11px] text-sky-600 dark:text-sky-400">
                            Apex set: questions come from the Apex library. Apex can't be combined with other languages.
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Questions */}
                    <div className="space-y-3">
                      <p className="text-sm font-medium dark:text-white text-textPrimary">Questions <span className="text-red-500">*</span></p>

                      <div className="space-y-3">
                        {questionSlots.length === 0 && isLoadingProblems ? (
                          [1, 2].map((i) => <div key={i} className="h-[60px] rounded-xl bg-gray-100 dark:bg-white/[0.06] animate-pulse" />)
                        ) : questionSlots.map((slot, idx) => {
                          const isExpanded = expandedSlotIdx === idx;
                          const isSlotInvalid = questionsInvalid && slot.problemIds.length === 0;
                          const slotProblems = slot.problemIds
                            .map((pid) => selectedProblems.find((p) => p.problem_id === pid))
                            .filter(Boolean);
                          return (
                            <div key={slot.id} className={`rounded-xl border dark:glass-effect bg-bgPrimary dark:shadow-lg shadow-sm transition-colors duration-150 ${isSlotInvalid ? "border-red-400 dark:border-red-500/60 ring-1 ring-red-400/30" : "border-gray-700/50"}`}>
                              <div className="w-full flex items-center gap-2 px-4 py-3">
                                <button
                                  type="button"
                                  onClick={() => toggleSlot(idx)}
                                  className="flex-1 flex items-center justify-between gap-3 text-left min-w-0"
                                >
                                  <div className="min-w-0">
                                    <p className="text-sm font-semibold dark:text-white text-gray-800">Question {idx + 1}</p>
                                    <p className={`text-xs mt-0.5 ${isSlotInvalid ? "text-red-400" : "text-gray-400 dark:text-gray-500"}`}>
                                      {slot.problemIds.length === 0
                                        ? "No problems added yet"
                                        : `${slot.problemIds.length} ${slot.problemIds.length === 1 ? "problem" : "problems"} added`}
                                    </p>
                                  </div>
                                  <ChevronDown
                                    size={16}
                                    className={`text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                                  />
                                </button>
                                {questionSlots.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() => removeQuestionSlot(idx)}
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-gray-300 dark:text-gray-600 hover:text-rose-500 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex-shrink-0 transition-all"
                                  >
                                    <X size={13} />
                                  </button>
                                )}
                              </div>

                              {isExpanded && (
                                <div className="border-t border-gray-100 dark:border-white/[0.07] px-4 py-4 space-y-4">

                                  {/* Filters */}
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="space-y-1">
                                      <label className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Difficulty level</label>
                                      <DropDownWithSearch
                                        dropdownData={DIFF_FILTER_OPTIONS}
                                        selectedValue={{ label: slotDiffFilter, value: slotDiffFilter }}
                                        onSelect={(item) => setSlotDiffFilter(item.value || "All")}
                                        placeholder="Select difficulty"
                                        showClearButton={false}
                                        className="w-full"
                                      />
                                    </div>
                                    <div className="space-y-1">
                                      <label className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">{isApex ? "Topic" : "DSA type"}</label>
                                      <div className="pill-multiselect coding-type-select">
                                        <MultiSelectDropdown
                                          key={track}
                                          options={[...topicOptions, "Uncategorized"]}
                                          value={slotTypeFilters.join(", ")}
                                          onChange={(val) => setSlotTypeFilters(val ? val.split(",").map((v) => v.trim()).filter(Boolean) : [])}
                                          placeholder={isApex ? "All topics" : "All types"}
                                          searchPlaceholder={isApex ? "Search topics…" : "Search types…"}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                  <p className="text-[11px] text-gray-400 dark:text-gray-500">
                                    {slotFiltersApplied ? "Filters applied — showing matching problems" : "No filters applied — showing all problems"}
                                  </p>

                                  <div className="border-t border-gray-100 dark:border-white/[0.07]" />

                                  {/* Library + Your set */}
                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="space-y-2 min-w-0">
                                      <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium dark:text-white text-textPrimary">Problem library</p>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            if (isApex) { setApexFormVisible(true); return; }
                                            setFormData(initialFormData);
                                            setFormVisible(true);
                                          }}
                                          className="flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 border border-violet-200 dark:border-violet-500/20 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors"
                                        >
                                          <Sparkles size={10} />Add Custom Question
                                        </button>
                                      </div>
                                      <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-0.5" style={{ scrollbarWidth: "thin", scrollbarColor: "#9CA3AF transparent" }}>
                                        {isLoadingProblems ? (
                                          [1, 2, 3].map((i) => <div key={i} className="h-[52px] rounded-xl bg-gray-100 dark:bg-white/[0.08] animate-pulse" />)
                                        ) : slotLibrary.selfHosted.length === 0 && slotLibrary.available.length === 0 ? (
                                          <div className="flex flex-col items-center justify-center py-8 gap-2 text-center">
                                            <Layers size={18} className="text-gray-300 dark:text-white/20" />
                                            <p className="text-xs text-gray-400 dark:text-gray-500">No problems match these filters</p>
                                          </div>
                                        ) : (
                                          [...slotLibrary.selfHosted, ...slotLibrary.available].map((problem) => (
                                            <div
                                              key={problem.problem_id}
                                              draggable
                                              onDragStart={(e) => {
                                                e.dataTransfer.setData("text/plain", problem.problem_id);
                                                e.dataTransfer.effectAllowed = "move";
                                              }}
                                              className="cursor-grab active:cursor-grabbing"
                                            >
                                              <ProblemCard
                                                problem={problem}
                                                action="add"
                                                onAction={(p) => handleAdd(p, idx)}
                                                onViewDetails={(p) => handleViewDetails(p, "add")}
                                                onDelete={handleDeleteProblem}
                                                compact
                                                selfHosted={librarySelfHosted.some((p) => p.problem_id === problem.problem_id)}
                                              />
                                            </div>
                                          ))
                                        )}
                                      </div>
                                    </div>
                                    <div className="space-y-2 min-w-0">
                                      <p className="text-sm font-medium dark:text-white text-textPrimary">Your set</p>
                                      {slotProblems.length > 1 && (
                                        <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-violet-50 dark:bg-violet-500/10 border border-violet-100 dark:border-violet-500/20">
                                          <Shuffle size={14} className="flex-shrink-0 mt-0.5 text-violet-500 dark:text-violet-400" />
                                          <p className="text-xs text-violet-700 dark:text-violet-300">
                                            <span className="font-semibold">Random pick</span> — each candidate only sees <span className="font-semibold">1</span> of these <span className="font-semibold">{slotProblems.length} problems</span>, chosen at random.
                                          </p>
                                        </div>
                                      )}
                                      <div
                                        onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverSlotIdx(idx); }}
                                        onDragLeave={() => setDragOverSlotIdx((cur) => (cur === idx ? null : cur))}
                                        onDrop={(e) => {
                                          e.preventDefault();
                                          setDragOverSlotIdx(null);
                                          const pid = e.dataTransfer.getData("text/plain");
                                          const problem = [...slotLibrary.selfHosted, ...slotLibrary.available].find((p) => p.problem_id === pid);
                                          if (problem) handleAdd(problem, idx);
                                        }}
                                        className={`space-y-1.5 h-[400px] overflow-y-auto rounded-xl border border-dashed p-2 transition-colors duration-150 ${
                                          dragOverSlotIdx === idx
                                            ? "border-violet-400 dark:border-violet-500/60 bg-violet-50/40 dark:bg-violet-500/5"
                                            : "border-gray-200 dark:border-white/10"
                                        }`}
                                        style={{ scrollbarWidth: "thin", scrollbarColor: "#9CA3AF transparent" }}
                                      >
                                        {isLoadingProblems && slot.problemIds.length > 0 ? (
                                          [1, 2].map((i) => <div key={i} className="h-[52px] rounded-xl bg-gray-100 dark:bg-white/[0.08] animate-pulse" />)
                                        ) : slotProblems.length === 0 ? (
                                          <div className="flex flex-col items-center justify-center h-full gap-1.5 text-center">
                                            <p className="text-xs text-gray-400 dark:text-gray-500 px-4">Drag problems here, or use the eye or + button</p>
                                          </div>
                                        ) : (
                                          slotProblems.map((problem) => (
                                            <ProblemCard
                                              key={problem.problem_id}
                                              problem={problem}
                                              action="remove"
                                              onAction={() => handleRemove(problem)}
                                              onViewDetails={(p) => handleViewDetails(p, "remove")}
                                              compact
                                              selfHosted={codingProblemOrigins[problem.problem_id] === "selfHosted"}
                                            />
                                          ))
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={addQuestionSlot}
                        className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-xl border border-dashed border-gray-300 dark:border-white/[0.15] text-gray-400 dark:text-gray-500 hover:border-violet-300 dark:hover:border-violet-500/40 hover:text-violet-500 dark:hover:text-violet-400 hover:bg-violet-50/30 dark:hover:bg-violet-500/5 transition-all text-sm font-medium"
                      >
                        <Plus size={14} /> Add question
                      </button>
                    </div>

                </div>

                {/* Bottom bar */}
                <div className="flex-shrink-0 flex items-center justify-end pt-4 mt-4 border-t border-gray-100 dark:border-white/[0.06]">
                  <button
                    onClick={handleStep1Next}
                    className="button flex items-center gap-2 px-8 py-2.5"
                  >
                    <span>Next</span>
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                STEP 2 — Configuration (placeholder — more settings coming later)
            ════════════════════════════════════════════════════════════════ */}
            {step === 2 && (
              <div className="flex flex-col">
                <div className="w-full max-w-md py-6 space-y-5">
                  <p className="text-sm text-gray-400 dark:text-gray-500">Control how much time candidates get.</p>

                  <div className="space-y-2">
                    <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Time limit</p>
                    <DropDownWithSearch
                      dropdownData={TIME_LIMIT_OPTIONS}
                      selectedValue={{ label: `${codingTimeLimit} min`, value: codingTimeLimit }}
                      onSelect={(item) => setCodingTimeLimit(item.value || DEFAULT_TIME_LIMIT)}
                      placeholder="Select time limit"
                      showClearButton={false}
                      className="w-full"
                    />
                    {suggestedTimeLimit > 0 && (
                      <p className="text-sm text-gray-400 dark:text-gray-500">
                        Suggested: ~{suggestedTimeLimit} min based on the difficulty of your questions
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex-shrink-0 flex items-center justify-between pt-3 mt-1 border-t border-gray-100 dark:border-white/[0.06]">
                  <button onClick={() => setStep(1)} className="button flex items-center gap-2 px-7 py-2.5">
                    <span>Back</span>
                  </button>
                  <button onClick={() => setStep(3)} disabled={selectedProblems.length === 0} className="button flex items-center gap-2 px-8 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed">
                    <span>Next</span>
                  </button>
                </div>
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════════
                STEP 3 — Review
            ════════════════════════════════════════════════════════════════ */}
            {step === 3 && (
              <div className="flex flex-col">
                <div className="w-full py-6 space-y-6">

                  {/* Summary */}
                  <div className="rounded-xl border border-gray-700/50 dark:glass-effect bg-bgPrimary dark:shadow-lg shadow-sm p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <div className="space-y-1 min-w-0">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Set Name</p>
                        <p className="text-sm font-normal dark:text-white text-gray-800 truncate">{activeSetName || "—"}</p>
                      </div>
                      <div className="space-y-1 min-w-0">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Languages</p>
                        <p className="text-sm font-normal dark:text-white text-gray-800 truncate">{allowedLanguages.join(", ") || "—"}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Total Questions</p>
                        <p className="text-sm font-normal dark:text-white text-gray-800">{questionSlots.filter((s) => s.problemIds.length > 0).length}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-normal dark:text-[#DAC0FF] text-textSecondary">Time Limit</p>
                        <p className="text-sm font-normal dark:text-white text-gray-800">{codingTimeLimit} min</p>
                      </div>
                    </div>
                  </div>

                  {/* Question-wise breakdown */}
                  <div className="space-y-3">
                    <p className="text-sm font-normal dark:text-white text-textPrimary">Questions</p>
                    <div className="space-y-3">
                      {questionSlots.map((slot, idx) => {
                        const slotProblems = slot.problemIds
                          .map((pid) => selectedProblems.find((p) => p.problem_id === pid))
                          .filter(Boolean);
                        return (
                          <div key={slot.id} className="rounded-xl border border-gray-700/50 dark:glass-effect bg-bgPrimary dark:shadow-lg shadow-sm overflow-hidden">
                            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-white/[0.07]">
                              <p className="text-sm font-normal dark:text-white text-gray-800">Question {idx + 1}</p>
                              <span className="text-[11px] font-normal px-2.5 py-0.5 rounded-full bg-gray-100 dark:bg-white/[0.08] text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-white/10">
                                {slotProblems.length} {slotProblems.length === 1 ? "problem" : "problems"}
                              </span>
                            </div>
                            <div className="p-3 space-y-2">
                              {slotProblems.map((problem) => (
                                <div
                                  key={problem.problem_id}
                                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-gray-50/60 dark:bg-white/[0.03] border border-gray-100 dark:border-white/[0.07]"
                                >
                                  <DiffBadge difficulty={codingProblemOrigins[problem.problem_id] === "selfHosted" ? "Custom" : problem.difficulty} />
                                  <span className="text-sm font-normal dark:text-white text-textPrimary truncate">{problem.title}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                </div>

                <div className="flex-shrink-0 flex items-center justify-between pt-3 mt-1 border-t border-gray-100 dark:border-white/[0.06]">
                  <button onClick={() => setStep(2)} className="button flex items-center gap-2 px-7 py-2.5">
                    <span>Back</span>
                  </button>
                  <button
                    onClick={handleSaveSet}
                    disabled={selectedProblems.length === 0 || isSaving}
                    className="button flex items-center gap-2 px-7 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isSaving ? <><span><Loader2 size={14} className="animate-spin" /></span><span>Saving…</span></> : <><span><Save size={14} /></span><span>Save Set</span></>}
                  </button>
                </div>
              </div>
            )}

          </div>
          </>
          )}
        </div>
      </div>

      {detailProblem && (
        <ProblemDetailModal
          problem={detailProblem}
          action={detailAction}
          onAction={detailAction === "add" ? (p) => handleAdd(p, expandedSlotIdx) : handleRemove}
          onClose={handleCloseDetailModal}
          loading={detailLoading}
        />
      )}

      {apexFormVisible && (
        <HostApexProblem
          onSubmit={handleApexSubmit}
          onClose={() => setApexFormVisible(false)}
        />
      )}

      {formVisible && (
        <HostCodingProblem
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          setFormVisible={setFormVisible}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-bgPrimary/50 dark:bg-black/20"
            style={{ backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)" }}
            onClick={() => setDeleteTarget(null)}
          />
          <div
            className="w-full max-w-sm relative bg-bgSecondary rounded-2xl border border-white/10 shadow-2xl p-6 space-y-5 animate-[fadeInScale_0.2s_ease-out]"
            style={{ background: theme === "dark" ? "linear-gradient(to bottom right, rgba(15,18,42,0.94), rgba(10,9,28,0.90))" : "" }}
          >
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-rose-500/10 border border-rose-500/20 flex items-center justify-center shrink-0">
                <AlertTriangle size={18} className="text-rose-400" />
              </div>
              <div className="space-y-1">
                <h3 className="text-sm font-semibold dark:text-white text-textPrimary">Delete Coding Set</h3>
                <p className="text-xs dark:text-gray-400 text-textSecondary leading-relaxed">
                  You are about to permanently delete <span className="font-medium dark:text-gray-200 text-textPrimary">"{deleteTarget}"</span>. This action cannot be undone.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteTarget(null)} className="border-btn dark:border-btn-dark flex-1 px-4 py-2.5 rounded-full text-sm font-medium transition-all">Cancel</button>
              <button onClick={confirmDelete} className="button flex-1 flex items-center justify-center gap-2 px-4 py-2.5">
                <Trash2 size={14} /><span>Yes, Delete</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CreateProblem;
